import React, { useEffect, useRef, useState } from 'react';
import { useProviderSearch } from '@guryou/html-components';
import FilterListIcon from '@material-ui/icons/FilterList';

import AdminHeaderPortal from './AdminHeaderPortal';

// Helpers that place a page's search / filters / actions into the L2 SubHeader zones.
// State stays in the page (these only relocate the DOM via portals), so existing filter
// logic keeps working unchanged.

// Search field → left zone.
export const HeaderSearch = ({ children }) => <AdminHeaderPortal targetId="admin-header-search">{children}</AdminHeaderPortal>;

// Action buttons (＋New, download CSV, …) → right zone. AdminHeaderPortal itself applies
// the 2-visible-then-"⋮"-overflow collapse for the actions zone, so this (and any page
// that imports AdminHeaderPortal directly with the default targetId) gets it for free.
export const HeaderActions = ({ children }) => <AdminHeaderPortal targetId="admin-header-actions">{children}</AdminHeaderPortal>;

// Presentational "Filtri" button + dropdown panel — shared by the portal-based
// `HeaderFilters` below (legacy MultipleItemsContainer/ContainerHeader pages) and the
// in-page `PageToolbar` (Performance Hub pages, studioPrimitives.js) so there's one
// popover implementation rather than two independently-styled ones. `count` shows the
// active-filter badge; `children` are the page's own filter controls.
// `closeOnChange` (default false, so every existing caller keeps its current
// behavior — some filter panels use checkboxes/multi-select where auto-closing
// after the first pick would be wrong) auto-dismisses the panel once a control
// inside it fires a native `change` event, for pages whose filters are all
// single-value pickers where "choose one and you're done" is the expected flow.
export const FilterPopoverButton = ({ count = 0, label = 'Filtri', children, compact = false, closeOnChange = false, align = 'left', width }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDoc = e => {
      // Searchable selects render their menu in a portal outside this element.
      // Treat that portal as part of the filter popover so focusing the search
      // field or choosing an option does not dismiss the whole panel.
      const insideSelectPortal = e.target?.closest?.('[role="listbox"], .MuiPopover-root, .MuiMenu-root, .MuiAutocomplete-popper');
      if (ref.current && !ref.current.contains(e.target) && !insideSelectPortal) {
        setOpen(false);
      }
    };
    const onEsc = e => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[13px] font-medium transition-colors ${
          count > 0 ? 'border-brand-accent text-brand-accent' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
        }`}
      >
        <FilterListIcon style={{ fontSize: 18 }} />
        {label}
        {count > 0 && (
          <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-accent text-white text-[10px] font-bold flex items-center justify-center">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <div
          style={width ? { width, maxWidth: '92vw' } : undefined}
          onChange={closeOnChange ? () => setOpen(false) : undefined}
          className={`absolute ${
            align === 'right' ? 'right-0' : 'left-0'
          } top-[calc(100%+8px)] z-[1000] w-[min(92vw,560px)] rounded-xl border border-gray-200 bg-white shadow-xl ${compact ? 'p-2.5' : 'p-4'}`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// Filter controls → middle zone, behind a "Filtri" button that opens a dropdown panel
// (so the 52px bar never overflows). Registers `hasFilters` so SubHeader knows it has
// content to show (see SubHeader.js's auto-hide).
export const HeaderFilters = ({ count = 0, label = 'Filtri', children }) => {
  const { setHasFilters } = useProviderSearch();

  useEffect(() => {
    setHasFilters(true);
    return () => setHasFilters(false);
  }, [setHasFilters]);

  return (
    <AdminHeaderPortal targetId="admin-header-filters">
      <FilterPopoverButton count={count} label={label}>
        {children}
      </FilterPopoverButton>
    </AdminHeaderPortal>
  );
};
